export type TwoDigitString = `${number}${number}`
export type FourDigitString = `${number}${number}${number}${number}`

type YYYY = FourDigitString
type MM = TwoDigitString
type DD = TwoDigitString

export type DateISOString = `${YYYY}-${MM}-${DD}`

export function convertDateToISOString(date?: Date | null) {
  if (!date) return undefined
  try {
    return date.toISOString().split("T")[0] as DateISOString
  } catch (_) {
    // If date is an "invalid date", a range error is thrown
    return undefined
  }
}

export function convertISOStringToUTCDate(date: string) {
  const utcDateString = date.split("T")[0] + "T00:00:00Z"
  return new Date(utcDateString)
}

export function parseISODateString(date: string | null | undefined) {
  return date ? convertISOStringToUTCDate(date) : null
}

export interface DateValues {
  year?: FourDigitString
  month?: TwoDigitString
  day?: TwoDigitString
}

export function buildDateISOString({
  year,
  month,
  day,
}: DateValues): DateISOString | undefined {
  if (!(year && month && day)) return undefined
  return `${year}-${month}-${day}`
}

const DATE_ISO_REGEX = /^\d{4}-\d{2}-\d{2}$/
export function isValidISOString(date: string): date is DateISOString {
  if (!DATE_ISO_REGEX.test(date)) {
    return false
  }

  // A valid ISO string will convert to a Date and back
  // while maintaining its original value (minus the time)
  const compareDate = convertDateToISOString(new Date(date))
  return compareDate ? compareDate.startsWith(date) : false
}

const PAD = 2
export function calculateAge(birthDate: DateISOString): number {
  if (!isValidISOString(birthDate)) {
    throw new Error(`Invalid birthdate: ${birthDate}`)
  }

  const [birthYear, birthMonth, birthDay] = birthDate.split("-")

  const today = new Date()
  const todayYear = today.getUTCFullYear()
  const todayMonthDay = `${String(today.getUTCMonth() + 1).padStart(PAD, "0")}-${String(today.getUTCDate()).padStart(PAD, "0")}`
  const birthMonthDay = `${birthMonth}-${birthDay}`

  const age = todayYear - Number(birthYear)
  return todayMonthDay < birthMonthDay ? age - 1 : age
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

function getDateLocaleOrder() {
  // The actual date here does not matter;
  // a date object is just needed to get locale order
  const arbitraryDate = new Date()
  return dateFormatter.formatToParts(arbitraryDate)
}

interface FormatDateISOStringToUserLocaleOptions {
  removeLeadingZeroes?: boolean
  parts?: Intl.DateTimeFormatPart[] // Mostly used for testing
}

export function formatDateISOStringToUserLocale(
  date: DateISOString,
  options: FormatDateISOStringToUserLocaleOptions = {
    removeLeadingZeroes: true,
  }
) {
  if (!date) return ""

  let [year, month, day] = date.split("T")[0].split("-") // eslint-disable-line
  if (!year || !month || !day) return ""

  if (options.removeLeadingZeroes) {
    month = month[0] === "0" ? month[1] : month
    day = day[0] === "0" ? day[1] : day
  }

  const parts = options.parts ?? getDateLocaleOrder()
  const result = parts.map((part) => {
    if (part.type === "literal") return part.value

    if (part.type === "year") return year
    if (part.type === "month") return month
    if (part.type === "day") return day

    return undefined
  })

  return result.filter(Boolean).join("")
}
